import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { Layout } from "@/components/Layout";
import { ratesQueryOptions } from "@/components/CountryPage";
import { STATIC_ALIASES, buildAliasHead } from "@/lib/alias-routes";
import { buildCalculatorLd } from "@/lib/page-schemas";

const A = STATIC_ALIASES.find((x) => x.slug === "jewelry-wastage-calculator")!;
const JewelryEstimator = lazy(() =>
  import("@/components/GoldToolsSuite").then((m) => ({ default: m.JewelryEstimator })),
);

export const Route = createFileRoute("/jewelry-wastage-calculator")({
  head: () => {
    const base = buildAliasHead({ title: A.title, description: A.description, path: `/${A.slug}` });
    const url = `https://drhint.com/${A.slug}`;
    return {
      ...base,
      scripts: [
        ...(base.scripts ?? []),
        ...buildCalculatorLd({
          url,
          name: A.title,
          description: A.description,
          featureList: [
            "Compute making charge + wastage on any jewellery purchase",
            "Pakistan tola and India gram inputs",
            "Live 24K spot pricing — no stale rates",
            "Side-by-side compare three jewellers in one screen",
          ],
          howTo: {
            name: "How to estimate jewellery wastage and making charges",
            totalTime: "PT2M",
            steps: [
              { name: "Enter net jewellery weight and karat", text: "Type the weight printed on the jeweller's slip (grams or tola) and the karat (most Indian / Pakistani jewellery is 22K or 21K)." },
              { name: "Enter the quoted wastage percentage", text: "Wastage is usually 1–5% for machine-made and 5–10% for fully hand-crafted jewellery. Anything above 10% on plain (non-stone) jewellery is a red flag — note the figure here." },
              { name: "Enter the per-gram making charge", text: "Plain bangles typically attract PKR 800–1500 per gram in Pakistan or INR 300–600 in India. Stone-set pieces can go 2–3x higher. Enter what the jeweller quoted you." },
              { name: "Read your itemised total", text: "The calculator splits the bill into pure metal value (live spot × purity × weight), wastage rupees and making charges. Negotiate any line that looks above market." },
              { name: "Save the breakdown before paying", text: "Screenshot the breakdown. Most jewellers will lower wastage or making by 20–40% once you show them an itemised counter-quote." },
            ],
          },
          faqs: [
            { q: "Why is the wastage figure separate from making charges?", a: "Wastage is the percentage of gold supposedly 'lost' during crafting (cutting, polishing). Making is the labour charge per gram. Jewellers sometimes mix both — keeping them split lets you negotiate each independently." },
            { q: "Is wastage actually lost or kept by the jeweller?", a: "In modern workshops, almost all 'wastage' is recovered as filings and dust. The wastage % is mostly a margin line item. That is why high wastage on plain jewellery is negotiable — there is no actual gold loss to justify it." },
            { q: "Will the jeweller buy back wastage and making on resale?", a: "No. On buy-back the jeweller pays only for pure gold content at that day's rate. Wastage and making are sunk costs — the calculator shows them clearly so you can decide how much to spend." },
          ],
          trail: [
            { name: "Home", item: "https://drhint.com/" },
            { name: "Tools", item: "https://drhint.com/tools" },
            { name: "Jewelry Wastage Calculator", item: url },
          ],
        }),
      ],
    };
  },
  loader: ({ context }) => { void context.queryClient.prefetchQuery(ratesQueryOptions); },
  component: () => (
    <Layout>
      <section className="mx-auto max-w-6xl px-4 pt-10 pb-2">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/tools" className="hover:text-gold">Tools</Link> / Jewelry Wastage Calculator
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{A.title}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">{A.description}</p>
      </section>
      <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>}>
        <JewelryEstimator />
      </Suspense>
    </Layout>
  ),
});